import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "../Constants";

const StatsDashboard = () => {
  const [recentMinutes, setRecentMinutes] = useState(30);
  const formatNumber = (value) => Number(value || 0).toLocaleString();

  const { data: recentContent = [] } = useQuery(
    ["recentContent", recentMinutes],
    async () => {
      const res = await axios.get(
        `${BASE_URL}/api/admin/stats/recent-content`,
        {
          params: { minutes: recentMinutes },
        }
      );

      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: searchTerms = [] } = useQuery(["searchTerms"], async () => {
    const res = await axios.get(`${BASE_URL}/api/admin/stats/searches`, {
      params: { limit: 10 },
    });
    return res.data;
  });

  const { data: todayStats = [] } = useQuery(
    ["todayStats"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/summary/today`);
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: monthStats = {} } = useQuery(
    ["monthStats"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/summary/month`);
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: analyticsData = {} } = useQuery(
    ["analyticsData"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/analytics`);
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: overview = {} } = useQuery(
    ["overviewStats"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/overview`);
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: platformStats = [] } = useQuery(
    ["platformStats"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/platforms`, {
        params: { days: 7 },
      });
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: eventBreakdown = [] } = useQuery(
    ["eventBreakdown"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/event-breakdown`, {
        params: { days: 7 },
      });
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  const { data: topSearches = [] } = useQuery(
    ["topSearches"],
    async () => {
      const res = await axios.get(`${BASE_URL}/api/admin/stats/top-searches`, {
        params: { days: 30, limit: 10 },
      });
      return res.data;
    },
    {
      staleTime: 600000,
    }
  );

  return (
    <div className="container py-4">
      <h2 className="mb-4">📊 Gangadwar Admin Dashboard</h2>

      <div className="row mb-4">
        <div className="col-sm-6 col-xl-3 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h5 className="card-title">Last 24 Hours</h5>
              <p className="card-text mb-1">👥 Users: {formatNumber(overview.unique_users_24h)}</p>
              <p className="card-text mb-0">🕒 Sessions: {formatNumber(overview.sessions_24h)}</p>
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-xl-3 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h5 className="card-title">Content Activity</h5>
              <p className="card-text mb-1">📑 Views: {formatNumber(overview.content_views_24h)}</p>
              <p className="card-text mb-0">📂 Subcategories: {formatNumber(overview.subcategory_views_24h)}</p>
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-xl-3 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h5 className="card-title">Engagement</h5>
              <p className="card-text mb-1">🎧 Audio Plays: {formatNumber(overview.audio_plays_24h)}</p>
              <p className="card-text mb-0">📄 PDF Downloads: {formatNumber(overview.pdf_downloads_24h)}</p>
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-xl-3 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h5 className="card-title">Actions</h5>
              <p className="card-text mb-1">🔁 Related Clicks: {formatNumber(overview.related_clicks_24h)}</p>
              <p className="card-text mb-0">📤 Shares: {formatNumber(overview.shares_24h)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Recent Content (last {recentMinutes} mins)</span>
              <select
                className="form-select form-select-sm w-auto"
                value={recentMinutes}
                onChange={(e) => setRecentMinutes(Number(e.target.value))}
              >
                {[10, 30, 60].map((min) => (
                  <option key={min} value={min}>
                    {min} mins
                  </option>
                ))}
              </select>
            </div>
            <div className="table-responsive">
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Hits</th>
                    {/* <th>Time</th> */}
                  </tr>
                </thead>
                <tbody>
                  {recentContent.map((item, index) => (
                    <tr key={index}>
                      <td>{item.content_id}</td>
                      <td>{item.title_primary}</td>
                      <td>{item.hits}</td>
                      {/* <td>{item.time}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header">Last 10 Search Terms</div>
            <ul className="list-group list-group-flush">
              {searchTerms.map((term, index) => (
                <li key={index} className="list-group-item">
                  {term.search_term}
                  <br />
                  <small className="text-muted">
                    {new Date(term.created_date).toLocaleString()}
                  </small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">📅 Day-wise Stats (This Month)</h5>
              <div className="table-responsive">
                <table className="table table-bordered mb-3">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Content 📑</th>
                      <th>Searches 🔎</th>
                      <th>Audios 🎧</th>
                      <th>Subcategories 🎑</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayStats.map((day, index) => (
                      <tr key={index}>
                        <td>{day.date}</td>
                        <td>{day.count_content}</td>
                        <td>{day.search_count}</td>
                        <td>{day.audio_count}</td>
                        <td>{day.sub_category_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">📆 This Month</h5>
              <p className="card-text">
                📑 Content: {monthStats.count_content}
                <br />
                🎧 Audios: {monthStats.audio_count}
                <br />
                📂 Subcategories: {monthStats.sub_category_count}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">📈 Last 7 Days</h5>
              <p className="card-text">
                👥 Users: {formatNumber(analyticsData.users)}
                <br />
                🕒 Sessions: {formatNumber(analyticsData.sessions)}
                <br />
                ⚡ Events: {formatNumber(analyticsData.events)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">📱 Platform Split (7 Days)</h5>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Users</th>
                      <th>Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformStats.map((item, index) => (
                      <tr key={index}>
                        <td className="text-capitalize">{item.platform}</td>
                        <td>{formatNumber(item.users)}</td>
                        <td>{formatNumber(item.events)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">🧭 Event Breakdown (7 Days)</h5>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventBreakdown.map((item, index) => (
                      <tr key={index}>
                        <td>{item.event_type}</td>
                        <td>{formatNumber(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">🔎 Top Searches (30 Days)</h5>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Search Term</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSearches.map((item, index) => (
                      <tr key={index}>
                        <td>{item.search_term}</td>
                        <td>{formatNumber(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsDashboard;
